import React, { useCallback, useEffect, useState } from "react";
import { FormHelperText } from "@mui/material";
import FileUpload from "@components/FileUpload/FileUpload";
import type { FileUploadConditions } from "@components/FileUpload/FileUpload.types";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import FormItem from "@components/FormItem/FormItem";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  FOTT_SERVICE_REQUEST_FIELDS as F,
  MAX_BYPASS_ATTACHMENTS,
  terminalServiceDocumentTypeOptions,
} from "./request/constants";
import type { FottServiceRequestFormData } from "./request/schema";

type AttachmentFormValue = NonNullable<FottServiceRequestFormData[typeof F.ATTACHMENT]>[number];

type TerminalServiceDocumentsCardProps = {
  files: UploadEntry[];
  setFiles: (files: UploadEntry[]) => void;
  onUpload: (files: File[]) => void;
  onAttachmentsChange: (attachments: AttachmentFormValue[]) => void;
  maxFiles?: number;
  uploadConditions: FileUploadConditions;
  allowedFileExtensions: string[];
  disabled?: boolean;
  isUploading?: boolean;
};

const getAttachmentField = {
  documentType: (idx: number) => `${F.ATTACHMENT}.${idx}.documentType` as const,
  otherDocumentType: (idx: number) => `${F.ATTACHMENT}.${idx}.otherDocumentType` as const,
  issueDate: (idx: number) => `${F.ATTACHMENT}.${idx}.issueDate` as const,
  expirationDate: (idx: number) => `${F.ATTACHMENT}.${idx}.expirationDate` as const,
  documentName: (idx: number) => `${F.ATTACHMENT}.${idx}.documentName` as const,
  documentSummary: (idx: number) => `${F.ATTACHMENT}.${idx}.documentSummary` as const,
};

const isFileEntry = (value: unknown): value is File =>
  typeof File !== "undefined" && value instanceof File;

const getEntryUrl = (entry: UploadEntry | undefined) => {
  if (!entry || isFileEntry(entry)) return "";
  return typeof entry.url === "string" ? entry.url : "";
};

const getEntryName = (entry: UploadEntry | undefined, idx: number) => {
  if (!entry) return `document-${idx + 1}`;
  return typeof entry.name === "string" && entry.name.trim() ? entry.name : `document-${idx + 1}`;
};

const getDocumentKey = (file: UploadEntry, idx: number) => {
  const entryUrl = getEntryUrl(file);

  if (entryUrl) return entryUrl;

  if (isFileEntry(file)) {
    return [file.name, file.size, file.type, file.lastModified].join("|");
  }

  return `${getEntryName(file, idx)}-${idx}`;
};

const TerminalServiceDocumentsCard: React.FC<TerminalServiceDocumentsCardProps> = ({
  files,
  setFiles,
  onUpload,
  onAttachmentsChange,
  maxFiles = MAX_BYPASS_ATTACHMENTS,
  uploadConditions,
  allowedFileExtensions,
  disabled = false,
  isUploading = false,
}) => {
  const { t } = useTranslation();
  const { getValues, setValue, control } = useFormContext<FottServiceRequestFormData>();
  const [error, setError] = useState("");
  const isMaxFilesReached = files.length >= maxFiles;

  const { remove } = useFieldArray({
    control: control,
    name: F.ATTACHMENT,
  });

  useEffect(() => {
    const currentAttachments = [...(getValues(F.ATTACHMENT) || [])];
    let hasChanges = false;

    files.forEach((file, idx) => {
      const documentUrl = getEntryUrl(file);
      if (!documentUrl) return;

      const currentAttachment = currentAttachments[idx];
      const existingDocument = !isFileEntry(currentAttachment) ? currentAttachment : undefined;

      if (existingDocument?.documentUrl === documentUrl) return;

      const fileName = getEntryName(file, idx);

      currentAttachments[idx] = {
        documentName: fileName,
        documentUrl,
        documentType: existingDocument?.documentType ?? "",
        otherDocumentType: existingDocument?.otherDocumentType ?? "",
        issueDate: existingDocument?.issueDate ?? null,
        expirationDate: existingDocument?.expirationDate ?? null,
        documentSummary: existingDocument?.documentSummary ?? "",
      };
      hasChanges = true;
    });

    if (!hasChanges) return;

    setValue(F.ATTACHMENT, currentAttachments, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: false,
    });
    onAttachmentsChange(currentAttachments as AttachmentFormValue[]);
  }, [files, getValues, onAttachmentsChange, setValue]);

  const handleUpload = (incomingFiles: File[]) => {
    setError("");
    if (!incomingFiles?.length) return;

    onUpload(incomingFiles);
  };

  const handleReject = () => {
    const typesLabel = uploadConditions.format
      ?.map((type) => type.toUpperCase().replace(".", ""))
      .join(", ");

    setError(
      t("form-validation.certificate-size-and-type", {
        max: uploadConditions.maxSizeLabel,
        types: typesLabel,
      }),
    );
  };

  const validateUploadFiles = useCallback(
    (incomingFiles: File[]) => {
      const existingFilesCount = useUploadStore.getState().getFilesForKey(F.ATTACHMENT).length ?? 0;

      if (existingFilesCount + incomingFiles.length > maxFiles) {
        return t("form-validation.max-files", { max: maxFiles });
      }

      return null;
    },
    [maxFiles, t],
  );

  const handleRemoveFile = (idx: number) => {
    const nextUploadFiles = files.filter((_, index) => index !== idx);
    const currentFiles = [...(getValues(F.ATTACHMENT) || [])];
    const nextFiles = currentFiles.filter((_, index) => index !== idx);

    setFiles(nextUploadFiles);
    remove(idx);

    setValue(F.ATTACHMENT, nextFiles, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: false,
    });
    onAttachmentsChange(nextFiles as AttachmentFormValue[]);
  };

  const handleFieldChange = useCallback(
    (key: string, value: unknown) => {
      setValue(key as Parameters<typeof setValue>[0], value as Parameters<typeof setValue>[1], {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });

      onAttachmentsChange((getValues(F.ATTACHMENT) ?? []) as AttachmentFormValue[]);
    },
    [getValues, onAttachmentsChange, setValue],
  );

  const handleValidationResult = useCallback((message: string | null) => {
    setError(message ?? "");
  }, []);

  return (
    <div className="flex flex-col gap-4">
      <FileUpload
        testId="fott-service-request-attachment"
        onUpload={handleUpload}
        onReject={handleReject}
        name={F.ATTACHMENT}
        conditions={uploadConditions}
        files={files}
        setFiles={(nextFiles) => setFiles(nextFiles.slice(0, maxFiles))}
        maxFiles={maxFiles}
        allowedExtensions={allowedFileExtensions}
        disabled={disabled || isUploading || isMaxFilesReached}
        uploadDescription={t("upload.additional-documents-upload", {
          extensions: allowedFileExtensions.join(", "),
          size: uploadConditions.maxSizeLabel,
        })}
        validateDrop={(selectedCount) => {
          if (selectedCount > maxFiles) {
            return t("form-validation.max-files", { max: maxFiles });
          }
          return null;
        }}
        validateFiles={validateUploadFiles}
        onValidationResult={handleValidationResult}
      />

      {error && (
        <FormHelperText
          sx={{
            ml: 0,
            fontWeight: 500,
            fontSize: 12,
            marginY: "4px",
            height: "20px",
            color: "error.main",
          }}
        >
          {error}
        </FormHelperText>
      )}

      {files.map((file, idx) => {
        const currentDocument = getValues(F.ATTACHMENT)?.[idx];
        const documentKey = getDocumentKey(file, idx);
        const hasDocumentUrl = !isFileEntry(currentDocument) && !!currentDocument?.documentUrl;
        const isFileLoading = isUploading && !hasDocumentUrl && isFileEntry(file);
        const showOtherDocumentType =
          !isFileEntry(currentDocument) && currentDocument?.documentType === "OTHERS";
        const isCertificateDocument =
          !isFileEntry(currentDocument) && currentDocument?.documentType === "CERTIFICATE";
        const documentTestId = `fott-service-request-attachment-${idx}`;

        return (
          <div className="flex flex-col gap-3" key={documentKey}>
            <div
              className="min-h-12 overflow-hidden rounded border border-[#E6EDF2] bg-[#F7F8FA]"
              data-testid={`${documentTestId}-single-file`}
              key={documentKey}
            >
              <div key={`${documentKey}-file`}>
                <SingleFile
                  testId={`${documentTestId}-file`}
                  file={file}
                  fieldName={F.ATTACHMENT}
                  currentFileIndex={idx}
                  setFiles={() => undefined}
                  removeFn={() => handleRemoveFile(idx)}
                  isLoading={isFileLoading}
                  disabled={disabled || isUploading}
                />
              </div>
            </div>

            {hasDocumentUrl && (
              <>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <FormItem<FottServiceRequestFormData>
                    testId={`fott-service-request-attachment-${idx}-document-type`}
                    name={getAttachmentField.documentType(idx)}
                    type="select"
                    label={t("terminal-services.fott-service-request.fields.document-type")}
                    options={terminalServiceDocumentTypeOptions}
                    onFieldChange={handleFieldChange}
                    disabled={disabled}
                  />

                  {showOtherDocumentType ? (
                    <FormItem<FottServiceRequestFormData>
                      testId={`fott-service-request-attachment-${idx}-document-type-other`}
                      name={getAttachmentField.otherDocumentType(idx)}
                      type="text"
                      maxChars={30}
                      label={t("terminal-services.fott-service-request.fields.other-specify")}
                      onFieldChange={handleFieldChange}
                      disabled={disabled}
                      required
                    />
                  ) : (
                    <div className="hidden md:block" />
                  )}
                  <div className="md:col-span-2">
                    <FormItem<FottServiceRequestFormData>
                      testId={`fott-service-request-attachment-${idx}-document-name`}
                      name={getAttachmentField.documentName(idx)}
                      type="text"
                      maxChars={50}
                      label={t("terminal-services.fott-service-request.fields.document-name")}
                      onFieldChange={handleFieldChange}
                      disabled={disabled}
                    />
                  </div>
                  <FormItem<FottServiceRequestFormData>
                    testId={`fott-service-request-attachment-${idx}-issue-date`}
                    name={getAttachmentField.issueDate(idx)}
                    type="date-picker"
                    label={t("terminal-services.fott-service-request.fields.issue-date")}
                    onFieldChange={handleFieldChange}
                    timePicker={false}
                    dateConstraint="not-future"
                    disabled={disabled}
                  />

                  <FormItem<FottServiceRequestFormData>
                    testId={`fott-service-request-attachment-${idx}-expiration-date`}
                    name={getAttachmentField.expirationDate(idx)}
                    type="date-picker"
                    label={t("terminal-services.fott-service-request.fields.expiration-date")}
                    onFieldChange={handleFieldChange}
                    timePicker={false}
                    dateConstraint="not-past"
                    disabled={disabled}
                    required={isCertificateDocument}
                  />
                  <div className="md:col-span-2">
                    <FormItem<FottServiceRequestFormData>
                      testId={`fott-service-request-attachment-${idx}-document-summary`}
                      name={getAttachmentField.documentSummary(idx)}
                      type="text"
                      maxChars={300}
                      label={t("terminal-services.fott-service-request.fields.document-summary")}
                      onFieldChange={handleFieldChange}
                      disabled={disabled}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default TerminalServiceDocumentsCard;
